import { useState, useEffect } from 'react';
import { Phone, PhoneCall, PhoneOff, Mic, MicOff, Volume2, VolumeX, UserPlus, Maximize2, Minimize2, Play, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

const LiveCallConsole = () => {
  const [isMinimized, setIsMinimized] = useState(false); // Changed to false so it starts open
  const [activeCall, setActiveCall] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [callMode, setCallMode] = useState('normal');
  const [transcript, setTranscript] = useState([]);
  const [callDuration, setCallDuration] = useState(0);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (activeCall && !isOnHold) {
      const interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [activeCall, isOnHold]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const initiateCall = async () => {
    if (!phoneNumber) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/voice-ai/calls/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_config_id: 'default',
          phone_number: phoneNumber,
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setActiveCall({
          id: data.call_id,
          phone_number: phoneNumber,
          status: 'ringing'
        });
        setCallDuration(0);
        setTranscript([{ time: new Date(), speaker: 'System', text: `Calling ${phoneNumber}...` }]);
      }
    } catch (err) {
      console.error('Failed to initiate call:', err);
    }
  };

  const endCall = () => {
    setActiveCall(null);
    setCallDuration(0);
    setTranscript([]);
    setPhoneNumber('');
    setCallMode('normal');
  };

  const toggleMute = () => setIsMuted(!isMuted);
  const toggleHold = () => setIsOnHold(!isOnHold);
  const addDigit = (digit) => setPhoneNumber(prev => prev + digit);

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button 
          onClick={(e) => {
            e.preventDefault();
            setIsMinimized(false);
          }} 
          className="rounded-full h-16 w-16 shadow-lg"
          size="lg"
        >
          <Phone className="h-6 w-6" />
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 shadow-2xl">
      <Card className="border-2">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Phone className="h-5 w-5" />Live Call Console
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setIsMinimized(true)}>
              <Minimize2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {!activeCall ? (
            <div className="space-y-4">
              <Input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="Enter phone number" className="text-center text-lg font-mono" />
              <div className="grid grid-cols-3 gap-2">
                {[1,2,3,4,5,6,7,8,9,'*',0,'#'].map((digit) => (
                  <Button key={digit} variant="outline" onClick={() => addDigit(digit.toString())} className="h-12 text-lg font-semibold">{digit}</Button>
                ))}
              </div>
              <Button onClick={initiateCall} disabled={!phoneNumber} className="w-full h-12 text-lg" size="lg">
                <PhoneCall className="h-5 w-5 mr-2" />Call
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center py-4 bg-secondary/50 rounded-lg">
                <div className="text-2xl font-bold mb-1">{activeCall.phone_number}</div>
                <div className="text-sm text-muted-foreground">{formatDuration(callDuration)}</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button variant={isMuted ? 'destructive' : 'outline'} onClick={toggleMute} className="flex flex-col items-center gap-1 h-16">
                  {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                  <span className="text-xs">{isMuted ? 'Unmute' : 'Mute'}</span>
                </Button>
                <Button variant={isOnHold ? 'secondary' : 'outline'} onClick={toggleHold} className="flex flex-col items-center gap-1 h-16">
                  {isOnHold ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                  <span className="text-xs">{isOnHold ? 'Resume' : 'Hold'}</span>
                </Button>
                <Button variant="outline" className="flex flex-col items-center gap-1 h-16">
                  <UserPlus className="h-5 w-5" />
                  <span className="text-xs">Transfer</span>
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant={callMode === 'whisper' ? 'default' : 'outline'} onClick={() => setCallMode('whisper')} size="sm">Whisper Mode</Button>
                <Button variant={callMode === 'barge' ? 'default' : 'outline'} onClick={() => setCallMode('barge')} size="sm">Barge Mode</Button>
              </div>
              <div>
                <div className="text-sm font-medium mb-2">Live Transcript</div>
                <ScrollArea className="h-32 border rounded-lg p-3">
                  {transcript.map((entry, idx) => (
                    <div key={idx} className="text-xs mb-2">
                      <span className="font-semibold">{entry.speaker}:</span>
                      <span className="ml-1">{entry.text}</span>
                    </div>
                  ))}
                </ScrollArea>
              </div>
              <Button onClick={endCall} variant="destructive" className="w-full h-12">
                <PhoneOff className="h-5 w-5 mr-2" />End Call
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LiveCallConsole;